import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const COLS = [
  { key: "name", label: "Lab" },
  { key: "samples_today", label: "Today" },
  { key: "samples_7d", label: "7 days" },
  { key: "samples_30d", label: "30 days" },
  { key: "avg_tat_minutes", label: "Avg TAT (min)" },
  { key: "tat_breach_pct", label: "TAT breach %" },
  { key: "critical_open", label: "Critical open" },
  { key: "active_users", label: "Users" },
  { key: "personnel", label: "Staff" },
];

const Stat = ({ label, value, testid }) => (
  <div className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.12em] text-[#6B7280]">{label}</p>
    <p className="font-heading font-bold text-2xl text-[#111827] mt-1">{value}</p>
  </div>
);

export default function LabOverview() {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState({ key: "samples_30d", dir: -1 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/labs/overview", {
        params: { today: new Date().toLocaleDateString("en-CA") },
      });
      setData(res.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const list = [...(data?.labs || [])];
    return list.sort((a, b) => {
      const x = a[sort.key], y = b[sort.key];
      if (typeof x === "string") return sort.dir * x.localeCompare(y || "");
      return sort.dir * ((x ?? -1) - (y ?? -1));
    });
  }, [data, sort]);

  const max = Math.max(1, ...rows.map((r) => r.samples_30d || 0));

  return (
    <div data-testid="lab-overview-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Building2 className="h-6 w-6 text-[#002FA7] mt-1" />
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#111827]">Cross-lab overview</h1>
            <p className="text-sm text-[#6B7280] mt-1">
              Sample volume, turnaround time and open critical alerts for every lab on LabTrack.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={load} data-testid="overview-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6">
        <Stat label="Labs" value={data?.totals?.labs ?? "—"} testid="overview-total-labs" />
        <Stat label="Samples today" value={data?.totals?.samples_today ?? "—"} testid="overview-total-today" />
        <Stat label="Samples 30 days" value={data?.totals?.samples_30d ?? "—"} testid="overview-total-30d" />
        <Stat label="Critical open" value={data?.totals?.critical_open ?? "—"} testid="overview-total-critical" />
        <Stat label="Active users" value={data?.totals?.active_users ?? "—"} testid="overview-total-users" />
      </div>

      <div className="mt-6 rounded-md border border-[#E5E7EB] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="overview-table">
          <thead className="bg-[#F3F4F6]">
            <tr>
              {COLS.map((c) => (
                <th key={c.key} onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : -1 }))}
                  data-testid={`overview-sort-${c.key}`}
                  className="text-left font-medium text-[10px] uppercase tracking-[0.08em] text-[#6B7280]
                    px-3 py-2 cursor-pointer select-none whitespace-nowrap hover:text-[#002FA7]">
                  {c.label}{sort.key === c.key ? (sort.dir === -1 ? " ↓" : " ↑") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr><td colSpan={COLS.length} className="px-3 py-8 text-center text-[#6B7280]"
                data-testid="overview-empty">No labs yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.lab_id} className="border-t border-[#E5E7EB]" data-testid={`overview-row-${r.lab_id}`}>
                <td className="px-3 py-2">
                  <p className="text-[#111827]">{r.name}</p>
                  <p className="text-xs text-[#6B7280]">
                    {r.city || "—"} · <span className={r.status === "active" ? "text-[#2E7D32]" : "text-[#B58900]"}>{r.status}</span>
                  </p>
                  <div className="mt-1.5 h-1.5 w-28 rounded-full bg-[#F3F4F6]">
                    <div className="h-1.5 rounded-full bg-[#002FA7]"
                      style={{ width: `${Math.round(100 * (r.samples_30d || 0) / max)}%` }} />
                  </div>
                </td>
                <td className="px-3 py-2">{r.samples_today}</td>
                <td className="px-3 py-2">{r.samples_7d}</td>
                <td className="px-3 py-2">{r.samples_30d}</td>
                <td className="px-3 py-2">{r.avg_tat_minutes ?? "—"}</td>
                <td className="px-3 py-2">
                  <span className={(r.tat_breach_pct || 0) > 10 ? "text-[#C92A2A] font-medium" : ""}>
                    {r.tat_breach_pct === null || r.tat_breach_pct === undefined ? "—" : `${r.tat_breach_pct}%`}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className={r.critical_open > 0 ? "text-[#C92A2A] font-medium" : ""}>{r.critical_open}</span>
                </td>
                <td className="px-3 py-2">{r.active_users}</td>
                <td className="px-3 py-2">{r.personnel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data?.generated_at && (
        <p className="text-xs text-[#6B7280] mt-3" data-testid="overview-generated">
          Generated {new Date(data.generated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
